import React, { useEffect, useState, useCallback, useMemo } from "react";
import style from "../caterpilar-plague/caterpilar-plague.module.css";
import CaterPillarIamge from "../../../../images/caterpillar-colored.png";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ParkOutlinedIcon from "@mui/icons-material/ParkOutlined";
import logger from "../../../../utils/logger";
import BracellRowExtras from "./BracellRowExtras";
import BracellLevelPanel from "./BracellLevelPanel";
import { diagnoseBracellLeptocybe } from "../../config/bracellRegistryProfile";

function createRow(index, plantCount) {
  const pc = Math.max(1, plantCount);
  return {
    planta: (index % pc) + 1,
    parcela: Math.floor(index / pc) + 1,
    numeroGalhas: 0,
    desfolha_incidencia_percent: "0",
    dano_severidade: "Sem Dano",
  };
}

function groupByParcela(samples, plantCount) {
  const pc = Math.max(1, plantCount);
  const groups = [];
  for (let i = 0; i < samples.length; i += pc) {
    groups.push(samples.slice(i, i + pc));
  }
  return groups;
}

function BracellLeptocybe({ plague, infestation, data, plantCount, parcelas: parcelasProp }) {
  const [samples, setSamples] = useState([]);
  const isValidPlague =
    plague && typeof plague === "object" && plague.name && plague.scientific;

  const numParcelas = Math.max(1, Number(parcelasProp) || 2);
  const numPlantCount = Math.max(1, Number(plantCount) || 5);

  useEffect(() => {
    const total = numParcelas * numPlantCount;
    setSamples(Array.from({ length: total }, (_, i) => createRow(i, numPlantCount)));
  }, [numParcelas, numPlantCount]);

  const patchRow = useCallback((index, patch) => {
    setSamples((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  }, []);

  const bumpGalhas = (index, delta) => {
    setSamples((prev) => {
      const next = [...prev];
      const cur = parseInt(next[index].numeroGalhas, 10) || 0;
      next[index] = {
        ...next[index],
        numeroGalhas: Math.max(0, cur + delta),
      };
      return next;
    });
  };

  useEffect(() => {
    if (!data || !isValidPlague) return;
    data(samples);
  }, [samples, data, isValidPlague]);

  const { lines, displayText, levelKey, diagnostic, prob } = useMemo(() => {
    const groups = groupByParcela(samples, numPlantCount);
    const parcelaMeans = groups.map((group) => {
      const sumG = group.reduce((acc, s) => acc + (parseInt(s.numeroGalhas, 10) || 0), 0);
      return group.length > 0 ? sumG / group.length : 0;
    });
    const mean = parcelaMeans.length > 0
      ? parcelaMeans.reduce((a, b) => a + b, 0) / parcelaMeans.length
      : 0;

    const d = diagnoseBracellLeptocybe(mean, numParcelas);
    const disp = d.levelKey === "none" ? "-" : d.diagnostic;

    const ls = [];
    if (numParcelas > 1) {
      parcelaMeans.forEach((m, p) => {
        ls.push({ label: `Parcela ${p + 1} — Média de galhas`, value: m.toFixed(2) });
      });
      ls.push({ label: "Média geral de galhas por planta", value: mean.toFixed(2) });
    } else {
      ls.push({ label: "Média de galhas por planta", value: mean.toFixed(2) });
    }
    return {
      lines: ls,
      displayText: disp,
      levelKey: d.levelKey,
      diagnostic: d.diagnostic,
      prob: d.prob,
    };
  }, [samples, numPlantCount, numParcelas]);

  useEffect(() => {
    if (!isValidPlague) return;
    infestation({ diagnostic, prob });
  }, [diagnostic, prob, infestation, isValidPlague]);

  if (!isValidPlague) {
    logger.warn("Objeto de praga inválido:", plague);
    return null;
  }

  return (
    <div>
      <div className={style.card}>
        <div className={style["sample-plague"]}>
          <img src={CaterPillarIamge} alt="" />
          <div className={style["name-sample-plague"]}>
            <div>{plague.name}</div>
            <div>{plague.scientific}</div>
          </div>
        </div>

        <div className={style.samples}>
          {samples.map((sample, index) => {
            const parcelaStart = index % numPlantCount === 0;
            const parcelaNum = Math.floor(index / numPlantCount) + 1;
            return (
              <React.Fragment key={index}>
                {numParcelas > 1 && parcelaStart && (
                  <div style={{ fontWeight: "600", fontSize: "0.85rem", padding: "10px 0 4px", color: "#40a8c4" }}>
                    Parcela {parcelaNum}
                  </div>
                )}
                <div className={style["collected-sample-title"]}>
                  <div className={style["collected-sample-plant"]}>
                    <div className={style["collected-sample-plant-number"]}>
                      {sample.planta}° <ParkOutlinedIcon style={{ width: "0.8rem" }} />
                    </div>
                  </div>
                  <div className={style["div-number-caterpillar"]}>
                    <div className={style["collected-sample-plant-number-caterpillar"]}>
                      N° de galhas
                    </div>
                    <div className={style["container-button"]}>
                      <RemoveIcon
                        onClick={() => bumpGalhas(index, -1)}
                        className={style.icon}
                      />
                      <input
                        className={style["collected-sample-plant-number-caterpillar-input"]}
                        value={sample.numeroGalhas}
                        type="number"
                        min={0}
                        onChange={(e) =>
                          patchRow(index, {
                            numeroGalhas: Math.max(
                              0,
                              parseInt(e.target.value, 10) || 0
                            ),
                          })
                        }
                      />
                      <AddIcon
                        onClick={() => bumpGalhas(index, 1)}
                        className={style.icon}
                      />
                    </div>
                  </div>
                  <BracellRowExtras
                    desfolha_incidencia_percent={sample.desfolha_incidencia_percent}
                    dano_severidade={sample.dano_severidade}
                    onPatch={(p) => patchRow(index, p)}
                  />
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <BracellLevelPanel
        lines={lines}
        displayText={displayText}
        levelKey={levelKey}
      />
    </div>
  );
}

export default BracellLeptocybe;
